import React, { useState, useMemo, useEffect } from 'react';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import '../styles/ExpensesList.css';
import fetchApi from '../utils/api.js';
import { usePermissionContext } from '../contexts/PermissionContext.js';
import AirDatePicker from '../components/ui/AirDatePicker';
import SearchBar from '../components/ui/SearchBar';
import PageSkeleton from '../components/ui/PageSkeleton';
import DataTable from '../components/ui/DataTable';

const formatDateDDMMYYYY = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length === 3) {
        if (parts[0].length === 4) {
            // YYYY-MM-DD
            return `${parts[2]}-${parts[1]}-${parts[0]}`;
        }
    }
    return dateStr;
};

function ExpensesList({ onAddExpenses, onEditExpenses, onNavigateToProfile, isReport = false }) {
    const { hasPermission } = usePermissionContext();
    const [filters, setFilters] = useState({ startDate: '', endDate: '', partyType: '', partyId: '' });
    const [activeFilters, setActiveFilters] = useState({ startDate: '', endDate: '', partyType: '', partyId: '' });
    const [allParties, setAllParties] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(() => { const s = sessionStorage.getItem('lastPage_ExpensesList'); return s ? parseInt(s) : 1; });
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [data, setData] = useState([]);
    const [totalEntries, setTotalEntries] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [stats, setStats] = useState({ todayTotal: 0, monthTotal: 0, lastMonthTotal: 0, grandTotal: 0 });
    const [loading, setLoading] = useState(true);

    const fetchExpenses = async (search = '', page = 1, limit = 10, filterParams = {}) => {
        try {
            if (!search) setLoading(true);
            const q = new URLSearchParams();
            if (search) q.append('search', search);
            q.append('page', page);
            q.append('limit', limit);
            if (filterParams.startDate) q.append('startDate', filterParams.startDate);
            if (filterParams.endDate) q.append('endDate', filterParams.endDate);
            if (filterParams.partyType) q.append('partyType', filterParams.partyType);
            if (filterParams.partyId) q.append('partyId', filterParams.partyId);

            const result = await fetchApi(`/expense?${q.toString()}`);
            if (result.expenses && Array.isArray(result.expenses)) {
                setData(result.expenses);
                setTotalEntries(result.total || 0);
                setTotalPages(result.pages || 1);
                if (result.stats) setStats(result.stats);
            }
        } catch (error) {
            console.error("Fetch Error:", error);
            if (error.status !== 403) toast.error("Server error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const loadParties = async () => {
            try {
                const res = await fetchApi('/account/unified?limit=1000');
                const accountParties = (res.accounts || [])
                    .filter(a => {
                        const name = (a.partyName || a.account_name || '').toLowerCase();
                        return !name.includes('cash') && !name.includes('bank');
                    })
                    .map(a => ({
                        id: a.id,
                        name: a.partyName,
                        type: a.type || 'Account'
                    }));
                setAllParties(accountParties);
            } catch (err) {
                console.error("Error loading parties:", err);
            }
        };
        loadParties();
    }, []);

    useEffect(() => { sessionStorage.setItem('lastPage_ExpensesList', currentPage); }, [currentPage]);
    useEffect(() => {
        const t = setTimeout(() => fetchExpenses(searchQuery, currentPage, itemsPerPage, activeFilters), 200);
        return () => clearTimeout(t);
    }, [searchQuery, currentPage, itemsPerPage, activeFilters]);

    const handleFilterChange = (e) => { const { name, value } = e.target; setFilters(prev => ({ ...prev, [name]: value })); };
    const handleSubmit = () => { setActiveFilters({ ...filters }); setCurrentPage(1); };
    const handleReset = () => { setFilters({ startDate: '', endDate: '', partyType: '', partyId: '' }); setActiveFilters({ startDate: '', endDate: '', partyType: '', partyId: '' }); setSearchQuery(''); setCurrentPage(1); };
    const paginate = (p) => setCurrentPage(p);

    const handleDelete = (id) => {
        Swal.fire({ title: 'Delete this expense?', text: 'This will also remove the party ledger entry.', icon: 'warning', showCancelButton: true, confirmButtonColor: '#e53e3e', confirmButtonText: 'Yes, delete!' })
            .then(async (r) => {
                if (r.isConfirmed) {
                    try {
                        await fetchApi(`/expense/${id}`, { method: 'DELETE' });
                        toast.success("Expense deleted");
                        fetchExpenses(searchQuery, currentPage, itemsPerPage, activeFilters);
                    } catch (err) { toast.error(err.message || "Delete failed"); }
                }
            });
    };

    const handleExcelDownload = async () => {
        try {
            setLoading(true);
            const q = new URLSearchParams();
            if (searchQuery) q.append('search', searchQuery);
            q.append('limit', '0');
            if (activeFilters.startDate) q.append('startDate', activeFilters.startDate);
            if (activeFilters.endDate) q.append('endDate', activeFilters.endDate);
            if (activeFilters.partyType) q.append('partyType', activeFilters.partyType);
            if (activeFilters.partyId) q.append('partyId', activeFilters.partyId);

            const result = await fetchApi(`/expense?${q.toString()}`);
            const expensesToExport = result.expenses || [];

            const worksheetData = expensesToExport.map((r, idx) => ({
                'No.': idx + 1,
                'Party A/c': r.partyName || 'Company',
                'Expense Type': r.expenseTypeName || '-',
                'Date': r.date || '',
                'Expense Name': r.expenseName || '',
                'Invoice No': r.invoiceNo || '-',
                'Purpose': r.expensePurpose || '-',
                'Net Amt': parseFloat(r.netAmount || 0).toFixed(2),
                'GST Amt': parseFloat(r.gstAmount || 0).toFixed(2),
                'Total': parseFloat(r.rowTotal || 0).toFixed(2)
            }));

            const worksheet = XLSX.utils.json_to_sheet(worksheetData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Expenses');
            XLSX.writeFile(workbook, 'ExpensesList.xlsx');
            toast.success("Excel exported successfully!");
        } catch (error) {
            console.error("Excel download error:", error);
            toast.error("Failed to export Excel");
        } finally {
            setLoading(false);
        }
    };

    const handlePrint = async () => {
        try {
            setLoading(true);
            const q = new URLSearchParams();
            if (searchQuery) q.append('search', searchQuery);
            q.append('limit', '0');
            if (activeFilters.startDate) q.append('startDate', activeFilters.startDate);
            if (activeFilters.endDate) q.append('endDate', activeFilters.endDate);
            if (activeFilters.partyType) q.append('partyType', activeFilters.partyType);
            if (activeFilters.partyId) q.append('partyId', activeFilters.partyId);

            const result = await fetchApi(`/expense?${q.toString()}`);
            const expensesToPrint = result.expenses || [];

            const printWindow = window.open('', '', 'height=600,width=800');
            printWindow.document.write('<html><head><title>Expenses List</title>');
            printWindow.document.write('<style>');
            printWindow.document.write('@page { size: A4 landscape; margin: 10mm; }');
            printWindow.document.write('body { font-family: Arial, sans-serif; margin: 0; padding: 10px; }');
            printWindow.document.write('h1 { text-align: center; color: #0b3a54; font-size: 16px; margin-bottom: 10px; }');
            printWindow.document.write('table { width: 100%; border-collapse: collapse; margin-top: 5px; table-layout: auto; }');
            printWindow.document.write('th, td { border: 1px solid #ccc; padding: 6px 4px; text-align: left; font-size: 9px; word-break: break-word; }');
            printWindow.document.write('th { background-color: #dff5e8; color: #000; font-weight: 700; text-transform: uppercase; }');
            printWindow.document.write('td.right { text-align: right; }');
            printWindow.document.write('</style></head><body>');
            printWindow.document.write('<h1>Expenses List</h1>');
            printWindow.document.write('<table><thead><tr>');
            printWindow.document.write('<th>No.</th>');
            printWindow.document.write('<th>Party A/c</th>');
            printWindow.document.write('<th>Expense Type</th>');
            printWindow.document.write('<th>Date</th>');
            printWindow.document.write('<th>Expense Name</th>');
            printWindow.document.write('<th>Invoice No</th>');
            printWindow.document.write('<th>Purpose</th>');
            printWindow.document.write('<th>Net Amt</th>');
            printWindow.document.write('<th>GST Amt</th>');
            printWindow.document.write('<th>Total</th>');
            printWindow.document.write('</tr></thead><tbody>');

            expensesToPrint.forEach((r, index) => {
                printWindow.document.write('<tr>');
                printWindow.document.write(`<td>${index + 1}</td>`);
                printWindow.document.write(`<td>${r.partyName || 'Company'}</td>`);
                printWindow.document.write(`<td>${r.expenseTypeName || '-'}</td>`);
                printWindow.document.write(`<td>${r.date || ''}</td>`);
                printWindow.document.write(`<td>${r.expenseName || ''}</td>`);
                printWindow.document.write(`<td>${r.invoiceNo || '-'}</td>`);
                printWindow.document.write(`<td>${r.expensePurpose || '-'}</td>`);
                printWindow.document.write(`<td class="right">₹${parseFloat(r.netAmount || 0).toFixed(2)}</td>`);
                printWindow.document.write(`<td class="right">₹${parseFloat(r.gstAmount || 0).toFixed(2)}</td>`);
                printWindow.document.write(`<td class="right">₹${parseFloat(r.rowTotal || 0).toFixed(2)}</td>`);
                printWindow.document.write('</tr>');
            });

            printWindow.document.write('</tbody></table></body></html>');
            printWindow.document.close();
            printWindow.print();
        } catch (error) {
            console.error("Print error:", error);
            toast.error("Failed to print data");
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <PageSkeleton />;

    const reportColumns = [
        { key: 'partyName', label: 'NAME / TRADE NAME', render: (item) => (
            <span
                style={{ cursor: 'pointer', color: '#111827', textTransform: 'uppercase', fontWeight: 'normal' }}
                onClick={() => {
                    if (onNavigateToProfile) {
                        onNavigateToProfile(item.partyType || 'account', item.partyId, item.partyName || 'GENERAL EXPENSES');
                    }
                }}
            >
                {(item.partyName || 'GENERAL EXPENSES').toUpperCase()}
            </span>
        ) },
        { key: 'accountGroupName', label: 'ACCOUNT GROUP NAME', render: (item) => item.accountGroupName || 'Expense Account' },
        { key: 'expenseName', label: 'EXPENSE NAME', render: (item) => item.expenseName || '-' },
        { key: 'invoiceNo', label: 'INVOICE NO', render: (item) => item.invoiceNo || '' },
        { key: 'rowTotal', label: 'EXPENSE AMOUNT', cellStyle: () => ({ textAlign: 'right' }), render: (item) => (parseFloat(item.rowTotal) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) },
        { key: 'date', label: 'EXPENSE DATE', render: (item) => formatDateDDMMYYYY(item.date) },
        { key: 'expensePurpose', label: 'PURPOSE', render: (item) => item.expensePurpose || '' },
        { key: 'expenseTypeName', label: 'TYPE', render: (item) => item.expenseTypeName || '-' },
    ];

    const standardColumns = [
        { key: 'partyName', label: 'Party A/c', render: (item) => item.partyName || <span style={{ color: '#aaa' }}>Company</span> },
        { key: 'expenseTypeName', label: 'Expense Type', render: (item) => item.expenseTypeName || '-' },
        { key: 'date', label: 'Date' },
        { key: 'expenseName', label: 'Expense Name' },
        { key: 'invoiceNo', label: 'Invoice No', render: (item) => item.invoiceNo || '-' },
        { key: 'expensePurpose', label: 'Purpose', render: (item) => item.expensePurpose || '-' },
        { key: 'netAmount', label: 'Net Amt', cellStyle: () => ({ textAlign: 'right' }), render: (item) => `₹${(parseFloat(item.netAmount) || 0).toFixed(2)}` },
        { key: 'gstAmount', label: 'GST Amt', cellStyle: () => ({ textAlign: 'right' }), render: (item) => `₹${(parseFloat(item.gstAmount) || 0).toFixed(2)}` },
        { key: 'rowTotal', label: 'Total', cellStyle: () => ({ textAlign: 'right', fontWeight: '600', color: '#2d7d46' }), render: (item) => `₹${(parseFloat(item.rowTotal) || 0).toFixed(2)}` },
        { key: 'action', label: 'Action', render: (item) => (
            <div className="expenses-action-icons">
                {hasPermission('Expense', 'update') && (
                    <button className="expenses-icon-btn expenses-icon-edit" title="Edit" onClick={() => onEditExpenses(item)}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                            <path d="M18.5 2.5a2.121 2.121 0 1 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                        </svg>
                    </button>
                )}
                {hasPermission('Expense', 'delete') && (
                    <button className="expenses-icon-btn expenses-icon-delete" title="Delete" onClick={() => handleDelete(item._id)}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6"></polyline>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        </svg>
                    </button>
                )}
            </div>
        ) }
    ];

    const columns = isReport ? reportColumns : standardColumns;

    const footerRow = data.length > 0 ? (
        isReport ? (
            <tr style={{ backgroundColor: '#eaeaea', fontWeight: 'bold' }}>
                <td colSpan={4} style={{ textAlign: 'right', fontWeight: 'bold', padding: '12px 10px', color: '#111827' }}>TOTAL</td>
                <td style={{ textAlign: 'right', fontWeight: 'bold', padding: '12px 10px', color: '#2d7d46' }}>
                    {data.reduce((sum, item) => sum + (parseFloat(item.rowTotal) || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/-
                </td>
                <td></td>
                <td></td>
                <td></td>
            </tr>
        ) : (
            <tr style={{ backgroundColor: '#eaeaea', fontWeight: 'bold' }}>
                <td colSpan={6} style={{ textAlign: 'right', fontWeight: 'bold', padding: '12px 10px', color: '#111827' }}>TOTAL</td>
                <td style={{ textAlign: 'right', fontWeight: 'bold', padding: '12px 10px', color: '#111827' }}>
                    ₹{data.reduce((sum, item) => sum + (parseFloat(item.netAmount) || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </td>
                <td style={{ textAlign: 'right', fontWeight: 'bold', padding: '12px 10px', color: '#111827' }}>
                    ₹{data.reduce((sum, item) => sum + (parseFloat(item.gstAmount) || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </td>
                <td style={{ textAlign: 'right', fontWeight: 'bold', padding: '12px 10px', color: '#2d7d46' }}>
                    ₹{data.reduce((sum, item) => sum + (parseFloat(item.rowTotal) || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </td>
                <td></td>
            </tr>
        )
    ) : null;

    return (
        <div className="expenses-list-container">
            {/* Header with Title and Action Buttons */}
            <div className="expenses-list-header">
                <h1 className="expenses-title-text">{isReport ? 'Expense Report' : 'Expenses List'}</h1>
                <div className="expenses-action-buttons">
                    {hasPermission('Expense', 'report') && (
                        <>
                            <button className="expenses-btn-excel" onClick={handleExcelDownload}>EXCEL</button>
                            <button className="expenses-btn-print" onClick={handlePrint}>PRINT</button>
                        </>
                    )}
                    {!isReport && hasPermission('Expense', 'add') && (
                        <button className="expenses-btn-add-new" onClick={onAddExpenses}>+ ADD EXPENSE</button>
                    )}
                </div>
            </div>

            {/* Toolbar: Search (Left) & Filters (Right) */}
            <div className="expenses-toolbar-row">
                <SearchBar
                    value={searchQuery}
                    onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                    placeholder="Search expenses..."
                />

                {/* Filters - Right Side */}
                <div className="expenses-toolbar-filters">
                    <div className="expenses-toolbar-filter-item">
                        <label className="expenses-toolbar-label">Start Date</label>
                        <AirDatePicker
                            className="expenses-toolbar-date-picker"
                            value={filters.startDate}
                            onChange={(v) => setFilters(p => ({ ...p, startDate: v }))}
                            placeholder="Start Date"
                        />
                    </div>
                    <div className="expenses-toolbar-filter-item">
                        <label className="expenses-toolbar-label">End Date</label>
                        <AirDatePicker
                            className="expenses-toolbar-date-picker"
                            value={filters.endDate}
                            onChange={(v) => setFilters(p => ({ ...p, endDate: v }))}
                            placeholder="End Date"
                        />
                    </div>
                    <div className="expenses-toolbar-filter-item">
                        <label className="expenses-toolbar-label">Party A/c</label>
                        <select
                            className="expenses-toolbar-select-field"
                            name="partyId"
                            value={filters.partyId}
                            onChange={handleFilterChange}
                        >
                            <option value="">---Select Party A/c---</option>
                            {allParties.map(p => (
                                <option key={`account-${p.id}`} value={p.id}>{p.name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="expenses-toolbar-buttons">
                        <button className="expenses-toolbar-btn-submit" onClick={handleSubmit}>Submit</button>
                        <button className="expenses-toolbar-btn-reset" onClick={handleReset}>Reset</button>
                    </div>
                </div>
            </div>

            {/* Stats Cards */}
            {!isReport && (
                <div className="expenses-stats-cards">
                    {[
                        { label: "Today Expenses", value: stats.todayTotal },
                        { label: "This Month", value: stats.monthTotal },
                        { label: "Last Month", value: stats.lastMonthTotal },
                        { label: "Total Expenses", value: stats.grandTotal }
                    ].map((c, i) => (
                        <div key={i} className="expenses-card">
                            <div className="expenses-card-header">
                                <span className="expenses-amount">₹ {(parseFloat(c.value) || 0).toFixed(2)} /-</span>
                                <span className="expenses-currency-icon">Rs</span>
                            </div>
                            <div className="expenses-label">{c.label}</div>
                        </div>
                    ))}
                </div>
            )}

            {/* Table */}
            <DataTable
                columns={columns}
                data={data}
                loading={loading}
                emptyMessage="No data available in table"
                page={currentPage}
                pageSize={itemsPerPage}
                totalEntries={totalEntries}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                onPageSizeChange={setItemsPerPage}
                footerRow={footerRow}
            />
        </div>
    );
}

export default ExpensesList;
